import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import Select from "react-select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { organizationsApi, usageApi } from "@/apis";
import type { Organization, UsageAppItem } from "@/apis";

interface AppUsageUsersProps {
  appId: string;
}


// Static column definitions for each tab
const USER_COLUMNS = [
  { key: "user_name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "prospect_company_count", label: "Prospect Companies Ingested" },
  { key: "seller_company_count", label: "Seller Companies Ingested" },
  { key: "simulation_count", label: "Simulations" },
  // { key: "last_activity_at", label: "Last Activity", format: "datetime" },
];

const COMPANY_COLUMNS = [
  { key: "company_name", label: "Company Name" },
  { key: "website", label: "Website" },
  { key: "user_name", label: "User" },
  { key: "entity_key", label: "Entity" },
  { key: "last_activity", label: "Ingested At", format: "datetime" },
];

const SIMULATION_COLUMNS = [
  { key: "user_name", label: "User" },
  { key: "seller_company", label: "Seller Company" },
  { key: "prospect_company", label: "Prospect Company" },
  { key: "last_activity", label: "Simulation Started At", format: "datetime" },
];

function formatCellValue(value: unknown, format?: string): string {
  if (value === null || value === undefined || value === "") return "-";
  if (format === "datetime" && typeof value === "string") {
    return new Date(value).toLocaleString();
  }
  return String(value);
}

export function AppUsageUsers({ appId }: AppUsageUsersProps) {
  const [app, setApp] = useState<UsageAppItem | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  // Users tab state
  const [users, setUsers] = useState<any>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [organizationId, setOrganizationId] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  // Companies tab state
  const [companies, setCompanies] = useState<any>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState("");
  const [companiesPage, setCompaniesPage] = useState(1);
  const [companiesTotal, setCompaniesTotal] = useState(0);

  // Simulations tab state
  const [simulations, setSimulations] = useState<any>([]);
  const [simulationsLoading, setSimulationsLoading] = useState(false);
  const [simulationsError, setSimulationsError] = useState("");
  const [simulationsPage, setSimulationsPage] = useState(1);
  const [simulationsTotal, setSimulationsTotal] = useState(0);

  const limit = 30;



  const loadUsers = async (
    page: number = 1,
    searchFilter?: string,
    orgFilter?: string,
    fromDateFilter?: string,
    toDateFilter?: string
  ) => {
    setUsersLoading(true);
    setUsersError("");
    try {
      const data = await usageApi.listUsers(appId, {
        page,
        limit,
        search: (searchFilter ?? search) || undefined,
        organization_id: (orgFilter ?? organizationId) || undefined,
        from_date: (fromDateFilter ?? fromDate) || undefined,
        to_date: (toDateFilter ?? toDate) || undefined,
      });
      setUsers(data?.users ?? []);
      setUsersTotal(data.total_count ?? 0);
      setUsersPage(data.page ?? page);
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : "Failed to load users");
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadCompanies = async (
    page: number = 1,
    searchFilter?: string,
    orgFilter?: string,
    fromDateFilter?: string,
    toDateFilter?: string
  ) => {
    setCompaniesLoading(true);
    setCompaniesError("");
    try {
      const data = await usageApi.getCompanies(appId, {
        page,
        limit,
        search: (searchFilter ?? search) || undefined,
        organization_id: (orgFilter ?? organizationId) || undefined,
        from_date: (fromDateFilter ?? fromDate) || undefined,
        to_date: (toDateFilter ?? toDate) || undefined,
      });
      setCompanies(data.companies ?? []);
      setCompaniesTotal(data.total_count ?? 0);
      setCompaniesPage(data.page ?? page);
    } catch (err) {
      setCompaniesError(err instanceof Error ? err.message : "Failed to load companies");
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const loadSimulations = async (
    page: number = 1,
    searchFilter?: string,
    orgFilter?: string,
    fromDateFilter?: string,
    toDateFilter?: string
  ) => {
    setSimulationsLoading(true);
    setSimulationsError("");
    try {
      const data = await usageApi.getSimulations(appId, {
        page,
        limit,
        search: (searchFilter ?? search) || undefined,
        organization_id: (orgFilter ?? organizationId) || undefined,
        from_date: (fromDateFilter ?? fromDate) || undefined,
        to_date: (toDateFilter ?? toDate) || undefined,
      });
      setSimulations(data.simulations ?? []);
      setSimulationsTotal(data.total_count ?? 0);
      setSimulationsPage(data.page ?? page);
    } catch (err) {
      setSimulationsError(err instanceof Error ? err.message : "Failed to load simulations");
      setSimulations([]);
    } finally {
      setSimulationsLoading(false);
    }
  };

  // Load all data with filters
  const loadAllData = async (
    page: number = 1,
    searchFilter?: string,
    orgFilter?: string,
    fromDateFilter?: string,
    toDateFilter?: string
  ) => {
    void loadUsers(page, searchFilter, orgFilter, fromDateFilter, toDateFilter);
    void loadCompanies(page, searchFilter, orgFilter, fromDateFilter, toDateFilter);
    void loadSimulations(page, searchFilter, orgFilter, fromDateFilter, toDateFilter);
  };

  // Load organizations for filter
  useEffect(() => {
    const loadOrgs = async () => {
      try {
        const data = await organizationsApi.list({ page: 1, limit: 500 });
        setOrganizations(data.items ?? []);
      } catch {
        setOrganizations([]);
      }
    };
    void loadOrgs();
  }, []);

  // Load initial data
  useEffect(() => {
    void loadAllData(1);
  }, [appId]);

  const organizationOptions = useMemo(
    () =>
      organizations.map((org) => ({
        value: org.organizationId,
        label: org.organizationName,
      })),
    [organizations]
  );

  const applyFilters = () => {
    const trimmedSearch = searchInput.trim();
    setSearch(trimmedSearch);
    void loadAllData(
      1,
      trimmedSearch || undefined,
      organizationId || undefined,
      fromDate || undefined,
      toDate || undefined
    );
  };

  const getRowValue = (row: Record<string, unknown>, key: string): unknown => {
    if (key in row) return row[key];
    // Check in metrics object
    const metrics = row.metrics as Record<string, unknown> | undefined;
    if (metrics && key in metrics) return metrics[key];
    return undefined;
  };

  const renderTable = (
    columns: { key: string; label: string; format?: string }[],
    rows: Record<string, unknown>[],
    loading: boolean,
    error: string,
    emptyMessage: string
  ) => {
    if (loading) {
      return (
        <div className="flex h-[400px] items-center justify-center rounded-md border">
          <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex h-[400px] items-center justify-center rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      );
    }

    return (
      <div className="h-[400px] overflow-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted/90 backdrop-blur">
            <tr>
              {columns.map((col) => (
                <th key={col.key} className="px-3 py-2 text-left font-medium">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-6 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={String(row.id || row.user_id || idx)} className="border-t">
                  {columns.map((col) => (
                    <td key={col.key} className="px-3 py-2">
                      {formatCellValue(getRowValue(row, col.key), col.format)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPagination = (
    page: number,
    total: number,
    onPageChange: (p: number) => void,
    loading: boolean
  ) => {
    const totalPages = Math.max(1, Math.ceil(total / limit));
    return (
      <div className="flex items-center justify-between gap-3">
        {/* <p className="text-sm text-muted-foreground">
          Page {page} of {totalPages} ({total} total)
        </p> */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1 || loading}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages || loading}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* App Header with Static Metrics */}
      {app ? (
        <div className="rounded-lg border bg-card p-4">
          <div className="flex flex-wrap items-center gap-4">
            {app.icon ? (
              <img src={app.icon} alt={app.appName} className="h-10 w-10 rounded-md object-cover" />
            ) : null}
            <div className="flex-1">
              <h2 className="text-xl font-semibold">{app.appName}</h2>
            </div>
            {/* Static Summary Metrics */}
            <div className="flex gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold">{app.summary?.companies_ingested ?? 0}</p>
                <p className="text-xs text-muted-foreground">Companies Ingested</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{app.summary?.simulations_run ?? 0}</p>
                <p className="text-xs text-muted-foreground">Simulations</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{app.summary?.active_users ?? 0}</p>
                <p className="text-xs text-muted-foreground">Active Users</p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Shared Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap justify-end items-end gap-3">
            {/* <div className="min-w-[220px] flex-1">
              <label className="mb-1 block text-sm font-medium">Search</label>
              <Input
                placeholder="Search by name, email, company..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyFilters();
                }}
              />
            </div> */}
            <div className="min-w-[220px]">
              <label className="mb-1 block text-sm font-medium">Organization</label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder="All organizations"
                isClearable
                value={organizationOptions.find((o) => o.value === organizationId) ?? null}
                onChange={(selected) => setOrganizationId(selected?.value ?? "")}
                options={organizationOptions}
              />
            </div>
            <div className="min-w-[150px]">
              <label className="mb-1 block text-sm font-medium">From Date</label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="min-w-[150px]">
              <label className="mb-1 block text-sm font-medium">To Date</label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <Button onClick={applyFilters}>Apply</Button>
            <Button
              variant="outline"
              size="default"
              onClick={() => {
                setOrganizationId("");
                setFromDate("");
                setToDate("");
                void loadAllData(1, undefined, undefined, undefined, undefined);
              }}
              disabled={usersLoading || companiesLoading || simulationsLoading}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Users</h3>
            {renderPagination(usersPage, usersTotal, (p) => void loadUsers(p), usersLoading)}
          </div>
        </CardHeader>
        <CardContent>
          {renderTable(
            USER_COLUMNS,
            users,
            usersLoading,
            usersError,
            "No users found."
          )}
        </CardContent>
      </Card>

      {/* Companies Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Companies</h3>
            {renderPagination(companiesPage, companiesTotal, (p) => void loadCompanies(p), companiesLoading)}
          </div>
        </CardHeader>
        <CardContent>
          {renderTable(
            COMPANY_COLUMNS,
            companies,
            companiesLoading,
            companiesError,
            "No companies found."
          )}
        </CardContent>
      </Card>

      {/* Simulations Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Simulations</h3>
            {renderPagination(simulationsPage, simulationsTotal, (p) => void loadSimulations(p), simulationsLoading)}
          </div>
        </CardHeader>
        <CardContent>
          {renderTable(
            SIMULATION_COLUMNS,
            simulations,
            simulationsLoading,
            simulationsError,
            "No simulations found."
          )}
        </CardContent>
      </Card>
    </div>
  );
}
